/**
 * ImportWhatsAppSection.jsx
 * 3-step wizard to import WhatsApp exported conversations (.txt / .zip)
 * Step 1 — Upload files
 * Step 2 — Preview & configure each conversation
 * Step 3 — Results
 */

import React, { useCallback, useRef, useState } from "react";
import { Icon } from "../shared/components";

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatTs(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return ts; }
}

function senderBadge(name, isCompany) {
  return (
    <span className={`waiSenderBadge${isCompany ? " waiSenderBadge--company" : ""}`}>
      {name}
    </span>
  );
}

// ── Step indicator ─────────────────────────────────────────────────────────────
function StepDots({ step }) {
  const labels = ["Upload", "Configurar", "Resultado"];
  return (
    <div className="waiStepDots">
      {labels.map((label, i) => (
        <React.Fragment key={i}>
          <div className={`waiStepDot${step === i + 1 ? " active" : step > i + 1 ? " done" : ""}`}>
            {step > i + 1 ? <Icon name="check" size={10} /> : i + 1}
            <span>{label}</span>
          </div>
          {i < labels.length - 1 && <div className="waiStepLine" />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Drop zone ─────────────────────────────────────────────────────────────────
function DropZone({ onFiles }) {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  function handleFiles(fileList) {
    const accepted = Array.from(fileList).filter(
      (f) => f.name.endsWith(".txt") || f.name.endsWith(".zip")
    );
    if (accepted.length) onFiles(accepted);
  }

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  }, []);

  return (
    <div
      className={`waiDropZone${dragging ? " waiDropZone--drag" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".txt,.zip"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div className="waiDropIcon">
        <Icon name="download" size={26} />
      </div>
      <div className="waiDropTitle">Arraste os arquivos aqui ou clique para selecionar</div>
      <div className="waiDropSub">
        Aceita arquivos <strong>.txt</strong> (exportação do WhatsApp) ou <strong>.zip</strong> com mídia
      </div>
      <div className="waiDropHint">
        No WhatsApp: Conversa → ⋮ → Mais → Exportar conversa
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ImportWhatsAppSection({ api }) {
  const [step, setStep]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  // Step 1 state
  const [files, setFiles]       = useState([]);

  // Step 2 state — previews returned from backend
  const [previews, setPreviews] = useState([]);   // [{ filename, messageCount, participants, sample, fromZip? }]
  const [configs, setConfigs]   = useState([]);   // [{ filename, companyParticipant, contactName, contactPhone }]

  // Step 3 state
  const [results, setResults]   = useState(null); // { results[], summary }

  // ── Helpers ────────────────────────────────────────────────────────────────
  function updateConfig(filename, field, value) {
    setConfigs((prev) =>
      prev.map((c) => (c.filename === filename ? { ...c, [field]: value } : c))
    );
  }

  function removeFile(name) {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  }

  // ── Step 1 → 2: Upload & Preview ──────────────────────────────────────────
  async function handlePreview() {
    if (!files.length) return;
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));

      const { data } = await api.post("/import/whatsapp/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setPreviews(data.files);
      // Build default configs
      setConfigs(
        data.files.map((pv) => ({
          filename:           pv.filename,
          companyParticipant: pv.participants[0]?.name || "",
          contactName:        pv.participants[1]?.name || pv.participants[0]?.name || "",
          contactPhone:       "",
        }))
      );
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao processar arquivos.");
    } finally {
      setLoading(false);
    }
  }

  // ── Step 2 → 3: Full Import ────────────────────────────────────────────────
  async function handleImport() {
    // Validate configs
    for (const cfg of configs) {
      if (!cfg.contactName?.trim()) {
        setError(`Informe o nome do contato para: ${cfg.filename}`);
        return;
      }
      if (!cfg.companyParticipant?.trim()) {
        setError(`Selecione qual participante é a sua empresa para: ${cfg.filename}`);
        return;
      }
    }

    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("configs", JSON.stringify(configs));

      const { data } = await api.post("/import/whatsapp/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setResults(data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao importar conversas.");
    } finally {
      setLoading(false);
    }
  }

  // ── Step 1 ─────────────────────────────────────────────────────────────────
  function renderStep1() {
    return (
      <div className="waiStep">
        <div className="cfgCard">
          <div className="cfgCardHdr">
            <div>
              <h3>Como exportar do WhatsApp</h3>
              <p>Siga os passos abaixo antes de importar</p>
            </div>
          </div>
          <ol className="waiHowTo">
            <li>Abra a conversa no WhatsApp</li>
            <li>Toque em <strong>⋮</strong> (Android) ou <strong>⋯</strong> (iOS) → <strong>Mais</strong></li>
            <li>Selecione <strong>Exportar conversa</strong></li>
            <li>Escolha <em>Com mídia</em> (para .zip) ou <em>Sem mídia</em> (para .txt)</li>
            <li>Envie o arquivo para o seu computador e carregue abaixo</li>
          </ol>
        </div>

        <div className="cfgCard" style={{ marginTop: 12 }}>
          <div className="cfgCardHdr">
            <div>
              <h3>Selecionar arquivos</h3>
              <p>Carregue um ou mais arquivos .txt ou .zip</p>
            </div>
          </div>

          <DropZone onFiles={(newFiles) => {
            setFiles((prev) => {
              const names = new Set(prev.map((f) => f.name));
              return [...prev, ...newFiles.filter((f) => !names.has(f.name))];
            });
          }} />

          {files.length > 0 && (
            <div className="waiFileList">
              {files.map((f) => (
                <div key={f.name} className="waiFileItem">
                  <Icon name={f.name.endsWith(".zip") ? "download" : "chat"} size={14} />
                  <span className="waiFileName">{f.name}</span>
                  <span className="waiFileSize">{(f.size / 1024).toFixed(0)} KB</span>
                  <button className="waiFileRemove" onClick={() => removeFile(f.name)}>
                    <Icon name="x" size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && <div className="waiError"><Icon name="bell" size={13} />{error}</div>}

          <div className="waiActions">
            <button
              className="btn btn-primary"
              onClick={handlePreview}
              disabled={!files.length || loading}
            >
              {loading ? "Processando…" : <>Analisar arquivos <Icon name="arrowRight" size={13} /></>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2 ─────────────────────────────────────────────────────────────────
  function renderStep2() {
    return (
      <div className="waiStep">
        {previews.map((pv) => {
          const cfg = configs.find((c) => c.filename === pv.filename) || {};
          return (
            <div key={pv.filename} className="cfgCard waiConvCard">
              <div className="cfgCardHdr">
                <div>
                  <h3 className="waiConvTitle">
                    <Icon name="chat" size={14} />
                    {pv.filename}
                    {pv.fromZip && <span className="waiZipBadge">de {pv.fromZip}</span>}
                  </h3>
                  <p>{pv.messageCount} mensagens · {pv.participants.length} participante(s)</p>
                </div>
              </div>

              {/* Participants */}
              <div className="waiParticipants">
                {pv.participants.map((p) => (
                  <div key={p.name} className="waiParticipantItem">
                    <span className="waiParticipantName">{p.name}</span>
                    <span className="waiParticipantCount">{p.count} msgs</span>
                  </div>
                ))}
              </div>

              {/* Config fields */}
              <div className="waiConfigGrid">
                <div className="waiConfigField">
                  <label>Qual participante é a sua empresa? *</label>
                  <select
                    value={cfg.companyParticipant || ""}
                    onChange={(e) => updateConfig(pv.filename, "companyParticipant", e.target.value)}
                  >
                    <option value="">Selecione…</option>
                    {pv.participants.map((p) => (
                      <option key={p.name} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                  <span className="waiConfigHint">Suas mensagens serão marcadas como "atendente"</span>
                </div>
                <div className="waiConfigField">
                  <label>Nome do contato *</label>
                  <input
                    value={cfg.contactName || ""}
                    onChange={(e) => updateConfig(pv.filename, "contactName", e.target.value)}
                    placeholder="Nome que aparecerá no CRM"
                  />
                  <span className="waiConfigHint">Será criado se não existir</span>
                </div>
                <div className="waiConfigField">
                  <label>Telefone do contato</label>
                  <input
                    value={cfg.contactPhone || ""}
                    onChange={(e) => updateConfig(pv.filename, "contactPhone", e.target.value)}
                    placeholder="5511999999999 (opcional)"
                  />
                  <span className="waiConfigHint">Permite vincular a contato existente</span>
                </div>
              </div>

              {/* Sample messages preview */}
              <div className="waiSampleTitle">Prévia das mensagens</div>
              <div className="waiSampleFeed">
                {pv.sample.map((msg, i) => {
                  const isCompany = msg.sender === cfg.companyParticipant;
                  return (
                    <div key={i} className={`waiSampleMsg${isCompany ? " waiSampleMsg--company" : ""}`}>
                      <div className="waiSampleMeta">
                        {senderBadge(msg.sender, isCompany)}
                        <span className="waiSampleTime">{formatTs(msg.ts)}</span>
                      </div>
                      <div className="waiSampleText">
                        {msg.isMedia ? <em className="waiMediaLabel"><Icon name="download" size={11} /> mídia</em> : msg.text}
                      </div>
                    </div>
                  );
                })}
                {pv.messageCount > 15 && (
                  <div className="waiSampleMore">+ {pv.messageCount - 15} mensagens não exibidas</div>
                )}
              </div>
            </div>
          );
        })}

        {error && <div className="waiError"><Icon name="bell" size={13} />{error}</div>}

        <div className="waiActions">
          <button className="btn btn-secondary" onClick={() => { setStep(1); setError(""); }}>
            ← Voltar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={loading}
          >
            {loading ? "Importando…" : <>Importar conversas <Icon name="arrowRight" size={13} /></>}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 3 ─────────────────────────────────────────────────────────────────
  function renderStep3() {
    if (!results) return null;
    const { summary, results: convResults } = results;

    return (
      <div className="waiStep">
        <div className="cfgCard waiResultCard">
          <div className="waiResultHeader">
            <div className="waiResultIcon"><Icon name="check" size={22} /></div>
            <div>
              <h3>Importação concluída!</h3>
              <p>Suas conversas foram adicionadas ao CRM</p>
            </div>
          </div>

          <div className="waiResultStats">
            <div className="waiResultStat">
              <strong>{summary.conversations}</strong>
              <span>conversa{summary.conversations !== 1 ? "s" : ""}</span>
            </div>
            <div className="waiResultStat">
              <strong>{summary.messagesInserted}</strong>
              <span>mensagens importadas</span>
            </div>
            {summary.messagesSkipped > 0 && (
              <div className="waiResultStat waiResultStat--warn">
                <strong>{summary.messagesSkipped}</strong>
                <span>mensagens com erro</span>
              </div>
            )}
          </div>

          <div className="waiResultList">
            {convResults.map((r, i) => (
              <div key={i} className="waiResultItem">
                <Icon name={r.ok ? "check" : "bell"} size={13} />
                <span className="waiResultFilename">{r.filename}</span>
                <span className="waiResultDetail">{r.inserted} mensagens importadas</span>
              </div>
            ))}
          </div>

          <div className="waiActions">
            <button
              className="btn btn-secondary"
              onClick={() => { setStep(1); setFiles([]); setPreviews([]); setConfigs([]); setResults(null); setError(""); }}
            >
              Importar mais conversas
            </button>
            <a href="/inbox" className="btn btn-primary">
              Ver no Inbox <Icon name="arrowRight" size={13} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="cfgSection">
      <div className="cfgCard" style={{ marginBottom: 16 }}>
        <div className="cfgCardHdr">
          <div>
            <h3>Importar conversas do WhatsApp</h3>
            <p>Carregue exportações do WhatsApp (.txt ou .zip) e importe para o CRM do Safe Zap</p>
          </div>
        </div>
        <StepDots step={step} />
      </div>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </div>
  );
}
